/**
 * EmergencyBlockPanel — Temporary access revocation for emergencies.
 *
 * Architecture:
 *   - SUBTRACTIVE ONLY: Can block, never grant
 *   - TIME-LIMITED: Max 72h, auto-expires
 *   - AUDITED: All blocks logged to Wasabi T1
 *   - REVIEW REQUIRED: Auto-creates task before expiry
 *
 * Normal scope changes go through YAML + git PR.
 * This panel is ONLY for "block now, sort out later" emergencies.
 */

import React, { useState, useCallback } from 'react';
import {
  Stack,
  Paper,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  Alert,
  Loader,
  Table,
  ActionIcon,
  Tooltip,
  Progress,
  Card,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconShieldOff,
  IconAlertTriangle,
  IconClock,
  IconRefresh,
  IconTrash,
  IconClockPause,
  IconUserOff,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

// Emergency blocks are stored in Valkey with TTL, queried via GraphQL
const LIST_EMERGENCY_BLOCKS = gql`
  query ListEmergencyBlocks {
    listEmergencyBlocks {
      blockId
      blockType
      targetId
      targetName
      reason
      blockedBy
      blockedAt
      expiresAt
      extensionCount
      scopesDenied
    }
  }
`;

const CREATE_EMERGENCY_BLOCK = gql`
  mutation CreateEmergencyBlock($input: CreateEmergencyBlockInput!) {
    createEmergencyBlock(input: $input) {
      blockId
      expiresAt
      reviewTaskId
    }
  }
`;

const EXTEND_BLOCK = gql`
  mutation ExtendEmergencyBlock($blockId: String!, $hours: Int!, $reason: String!) {
    extendEmergencyBlock(blockId: $blockId, hours: $hours, reason: $reason) {
      blockId
      expiresAt
      extensionCount
    }
  }
`;

const REMOVE_BLOCK = gql`
  mutation RemoveEmergencyBlock($blockId: String!, $reason: String!) {
    removeEmergencyBlock(blockId: $blockId, reason: $reason)
  }
`;

interface EmergencyBlock {
  blockId: string;
  blockType: 'actor' | 'role';
  targetId: string;
  targetName: string;
  reason: string;
  blockedBy: string;
  blockedAt: number;
  expiresAt: number;
  extensionCount: number;
  scopesDenied: string[];
}

interface EmergencyBlockPanelProps {
  actors?: Array<{ id: string; name: string }>;
  roles?: Array<{ id: string; name: string }>;
}

export function EmergencyBlockPanel({
  actors = [],
  roles = [],
}: EmergencyBlockPanelProps) {
  const [blockModalOpened, { open: openBlockModal, close: closeBlockModal }] = useDisclosure(false);
  const [extendingBlock, setExtendingBlock] = useState<EmergencyBlock | null>(null);

  const { data, loading, error, refetch } = useQuery(LIST_EMERGENCY_BLOCKS, {
    pollInterval: 30000, // Refresh every 30s to update countdowns
  });

  const [createBlock, { loading: creating }] = useMutation(CREATE_EMERGENCY_BLOCK, {
    onCompleted: () => {
      closeBlockModal();
      form.reset();
      refetch();
    },
  });

  const [extendBlock] = useMutation(EXTEND_BLOCK, {
    onCompleted: () => {
      setExtendingBlock(null);
      refetch();
    },
  });

  const [removeBlock] = useMutation(REMOVE_BLOCK, {
    onCompleted: () => refetch(),
  });

  const form = useForm({
    initialValues: {
      blockType: 'actor' as 'actor' | 'role',
      targetId: '',
      reason: '',
      durationHours: '24',
      scopesDenied: ['*'], // Block all by default
    },
    validate: {
      targetId: (v) => (!v ? 'Required' : null),
      reason: (v) => (!v || v.length < 10 ? 'Reason must be at least 10 characters' : null),
    },
  });

  const handleCreateBlock = useCallback(async (values: typeof form.values) => {
    await createBlock({
      variables: {
        input: {
          blockType: values.blockType,
          targetId: values.targetId,
          reason: values.reason,
          durationHours: parseInt(values.durationHours),
          scopesDenied: values.scopesDenied,
        },
      },
    });
  }, [createBlock, form]);

  const handleExtend = useCallback(async (block: EmergencyBlock, hours: number, reason: string) => {
    await extendBlock({
      variables: {
        blockId: block.blockId,
        hours,
        reason,
      },
    });
  }, [extendBlock]);

  const handleRemove = useCallback(async (block: EmergencyBlock) => {
    const reason = window.prompt('Reason for removing block (required for audit):');
    if (reason && reason.length >= 10) {
      await removeBlock({
        variables: {
          blockId: block.blockId,
          reason,
        },
      });
    } else if (reason) {
      alert('Reason must be at least 10 characters');
    }
  }, [removeBlock]);

  const blocks: EmergencyBlock[] = data?.listEmergencyBlocks || [];

  const getTimeRemaining = (expiresAt: number) => {
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return { text: 'Expired', percent: 0, color: 'red' };
    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const percent = Math.min(100, (remaining / (72 * 3600000)) * 100);
    const color = percent > 50 ? 'green' : percent > 20 ? 'yellow' : 'red';
    return { text: `${hours}h ${minutes}m`, percent, color };
  };

  const targetOptions = form.values.blockType === 'actor'
    ? actors.map((a) => ({ value: a.id, label: a.name }))
    : roles.map((r) => ({ value: r.id, label: r.name }));

  if (loading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading emergency blocks...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle />}>
        Failed to load blocks: {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={4}>Emergency Access Blocks</Title>
          <Text size="sm" c="dimmed">
            Temporary access revocation — expires automatically, requires review
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="subtle"
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconShieldOff size={16} />}
            color="red"
            onClick={openBlockModal}
          >
            Emergency Block
          </Button>
        </Group>
      </Group>

      <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Text size="sm" fw={500}>Emergency blocks are temporary (max 72h).</Text>
        <Text size="sm">
          For permanent access changes, submit a PR to modify role configs in git.
          Each block auto-creates a review task that must be resolved before expiry.
        </Text>
      </Alert>

      {blocks.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" variant="light" color="green">
              <IconClock size={24} />
            </ThemeIcon>
            <Text c="dimmed">No active emergency blocks</Text>
          </Stack>
        </Paper>
      ) : (
        <Stack gap="sm">
          {blocks.map((block) => {
            const time = getTimeRemaining(block.expiresAt);
            return (
              <Card key={block.blockId} withBorder p="md">
                <Group justify="space-between" mb="xs">
                  <Group>
                    <ThemeIcon color="red" variant="light">
                      {block.blockType === 'actor' ? <IconUserOff size={16} /> : <IconShieldOff size={16} />}
                    </ThemeIcon>
                    <div>
                      <Text fw={500}>{block.targetName}</Text>
                      <Text size="xs" c="dimmed">
                        {block.blockType === 'actor' ? 'User' : 'Role'} • Blocked by {block.blockedBy}
                      </Text>
                    </div>
                  </Group>
                  <Group>
                    <Badge color={time.color} variant="light">
                      <Group gap={4}>
                        <IconClock size={12} />
                        {time.text}
                      </Group>
                    </Badge>
                    {block.extensionCount > 0 && (
                      <Badge color="gray" variant="outline">
                        Extended {block.extensionCount}x
                      </Badge>
                    )}
                  </Group>
                </Group>

                <Progress value={time.percent} color={time.color} size="xs" mb="xs" />

                <Text size="sm" mb="sm">{block.reason}</Text>

                <Group justify="flex-end" gap="xs">
                  {block.extensionCount < 3 && (
                    <Tooltip label="Extend by 24h (max 3 extensions)">
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconClockPause size={14} />}
                        onClick={() => {
                          const reason = window.prompt('Reason for extension:');
                          if (reason && reason.length >= 10) {
                            handleExtend(block, 24, reason);
                          }
                        }}
                      >
                        Extend
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip label="Remove block (requires reason)">
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      onClick={() => handleRemove(block)}
                    >
                      Remove Block
                    </Button>
                  </Tooltip>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Create Block Modal */}
      <Modal
        opened={blockModalOpened}
        onClose={closeBlockModal}
        title={
          <Group>
            <ThemeIcon color="red">
              <IconShieldOff size={16} />
            </ThemeIcon>
            <Text fw={600}>Create Emergency Block</Text>
          </Group>
        }
        size="md"
      >
        <form onSubmit={form.onSubmit(handleCreateBlock)}>
          <Stack gap="md">
            <Alert color="red" variant="light">
              This will immediately revoke access. Use only for emergencies.
              A review task will be auto-created.
            </Alert>

            <Select
              label="Block Type"
              data={[
                { value: 'actor', label: 'Block User' },
                { value: 'role', label: 'Block Role (all users with this role)' },
              ]}
              {...form.getInputProps('blockType')}
            />

            <Select
              label={form.values.blockType === 'actor' ? 'User to Block' : 'Role to Block'}
              data={targetOptions}
              searchable
              placeholder="Search..."
              {...form.getInputProps('targetId')}
            />

            <Textarea
              label="Reason"
              description="Required for audit trail (min 10 characters)"
              placeholder="e.g., Pending investigation of incident #123"
              minRows={2}
              {...form.getInputProps('reason')}
            />

            <Select
              label="Duration"
              data={[
                { value: '4', label: '4 hours' },
                { value: '8', label: '8 hours' },
                { value: '24', label: '24 hours (default)' },
                { value: '48', label: '48 hours' },
                { value: '72', label: '72 hours (maximum)' },
              ]}
              {...form.getInputProps('durationHours')}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeBlockModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="red"
                loading={creating}
                leftSection={<IconShieldOff size={16} />}
              >
                Create Block
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
